import { DIRECTIONS, dx, dy, type Direction, type Turn } from '../../sim/direction';
import type { HerdSpec, SimLevel } from '../../sim/level';
import type { MowerSpec } from '../../sim/mower';
import { Simulation } from '../../sim/simulation';
import { GreedyPlanner } from './greedyPlanner';
import { motionModelFor, type Action, type MotionModel } from './motionModel';
import { NARROW_BODY, StateSpace } from './stateSpace';

export interface HerdResult {
  won: boolean;
  ticks: number;
  crashes: number;
  penned: number;
  /** Tile centers reached, with the fuel each step burned (for placing gas cans). */
  route: { x: number; y: number; fuel: number }[];
}

/**
 * Plays Round Up the Sheep: picks the sheep closest to the pen, works out its way there one nudge
 * at a time (only nudges that leave room to stand behind it), drives round to push it along that
 * way, and once every sheep is penned mows what is left. Drives the herd bot and the in-game
 * autopilot on herding lawns, and on a round-up match (docs/ROUNDUP_MATCH.md) herds for its own
 * pen.
 */
export class HerdPilot {
  private readonly model: MotionModel;
  private readonly space: StateSpace;
  private mower: GreedyPlanner | null = null;

  /** The pen to fill: the lawn's own, or (a round-up match) this seat's. */
  private readonly pen: HerdSpec | null;
  /** A round-up match: the other seats' gates, which a sheep is never pushed through. */
  private readonly otherGates: readonly { x: number; y: number }[];

  constructor(
    private readonly sim: Simulation,
    pen?: HerdSpec,
  ) {
    this.pen = pen ?? sim.level.herd ?? null;
    this.otherGates = (sim.level.roundup?.pens ?? [])
      .map((p) => p.gate)
      .filter((g) => g !== this.pen?.gate);
    this.model = motionModelFor(sim.mowerSpec);
    this.space = new StateSpace(sim.grid, this.model, NARROW_BODY(sim.mowerSpec.cutPower));
  }

  /** At a tile center: the turn to ask for (null to keep going straight). */
  decide(): Turn | null {
    const sim = this.sim;
    if (sim.mower.progress !== 0) return null;
    const loose = sim.critters.list.filter((c) => c.kind === 'sheep' && !c.penned);
    if (loose.length === 0 || !this.pen) {
      // Everyone home: mow the rest.
      this.mower ??= new GreedyPlanner(sim);
      const action = this.mower.nextAction();
      return !action || action === 'straight' ? null : action;
    }
    // The loose sheep with the shortest way to the pen; push it along that way.
    let best: { x: number; y: number; dir: Direction; length: number } | null = null;
    for (const c of loose) {
      const way = this.sheepWay(c.x, c.y);
      if (way && (!best || way.length < best.length)) best = { x: c.x, y: c.y, ...way };
    }
    if (best) {
      const path = this.pushPath(best.x, best.y, best.dir);
      const action = path?.[0];
      if (action) return action === 'straight' ? null : action;
    }
    // Nothing to push right now (a sheep wedged somewhere): keep mowing meanwhile.
    this.mower ??= new GreedyPlanner(sim);
    const action = this.mower.nextAction();
    return !action || action === 'straight' ? null : action;
  }

  private open(x: number, y: number): boolean {
    return (
      this.sim.grid.inBounds(x, y) && !this.sim.grid.isBlockedFor(x, y, this.sim.mowerSpec.cutPower)
    );
  }

  /**
   * A sheep's shortest way to the pen gate in nudges: each step needs room ahead for the sheep and
   * room behind it for the mower. Returns the first nudge's direction and the number of nudges.
   */
  private sheepWay(sx: number, sy: number): { dir: Direction; length: number } | null {
    const sim = this.sim;
    const w = sim.grid.width;
    const gate = this.pen!.gate;
    const goal = gate.y * w + gate.x;
    const others = new Set([
      ...sim.critters.list.filter((c) => !(c.x === sx && c.y === sy)).map((c) => c.y * w + c.x),
      ...this.otherGates.map((g) => g.y * w + g.x),
    ]);
    const first = new Map<number, Direction>();
    const dist = new Map<number, number>([[sy * w + sx, 0]]);
    const queue = [{ x: sx, y: sy }];
    for (let head = 0; head < queue.length; head++) {
      const p = queue[head]!;
      const here = p.y * w + p.x;
      if (here === goal) {
        return { dir: first.get(here)!, length: dist.get(here)! };
      }
      for (const d of DIRECTIONS) {
        const nx = p.x + dx(d);
        const ny = p.y + dy(d);
        const n = ny * w + nx;
        if (dist.has(n) || !this.open(nx, ny) || others.has(n)) continue;
        if (!this.open(p.x - dx(d), p.y - dy(d))) continue; // room for the mower behind
        dist.set(n, dist.get(here)! + 1);
        first.set(n, here === sy * w + sx ? d : first.get(here)!);
        queue.push({ x: nx, y: ny });
      }
    }
    return null;
  }

  /**
   * Actions from the mower's state to a nudge: driving straight into the sheep at (sx, sy) heading
   * `dir` (turning into a critter is refused, so the last move must be straight). Never through any
   * other critter.
   */
  private pushPath(sx: number, sy: number, dir: Direction): Action[] | null {
    const { space, model, sim } = this;
    const m = sim.mower;
    const start = space.index(m.x, m.y, m.dir, model.fromMower(m));
    const prev = new Map<number, { from: number; action: Action }>();
    const seen = new Set<number>([start]);
    const queue = [start];
    for (let head = 0; head < queue.length; head++) {
      const i = queue[head]!;
      const s = space.decode(i);
      if (i !== start && s.x === sx && s.y === sy && s.dir === dir) {
        const actions: Action[] = [];
        for (let k = i; k !== start; k = prev.get(k)!.from) actions.unshift(prev.get(k)!.action);
        return actions;
      }
      space.forEachSuccessor(i, (j, action) => {
        if (seen.has(j)) return;
        const t = space.decode(j);
        if (sim.critters.occupied(t.x, t.y)) {
          const isPush = t.x === sx && t.y === sy && t.dir === dir && action === 'straight';
          if (!isPush) return;
        }
        seen.add(j);
        prev.set(j, { from: i, action });
        queue.push(j);
      });
    }
    return null;
  }
}

/** Plays a herding lawn with the {@link HerdPilot}: can every sheep be penned and the lawn mowed? */
export function solveHerd(
  level: SimLevel,
  mower: MowerSpec,
  { maxTicks = 200_000, ignoreFuel = false }: { maxTicks?: number; ignoreFuel?: boolean } = {},
): HerdResult {
  const spec: MowerSpec =
    ignoreFuel && mower.fuelTiles !== undefined ? { ...mower, fuelTiles: 1_000_000_000 } : mower;
  const sim = new Simulation(level, spec);
  sim.drainEvents();
  const pilot = new HerdPilot(sim);
  let crashes = 0;
  const route = [{ x: sim.mower.x, y: sim.mower.y, fuel: 0 }];
  let fuel = sim.mower.fuel;
  while (sim.status === 'running' && sim.tick < maxTicks) {
    const turn = pilot.decide();
    if (turn) sim.enqueue({ type: 'turn', turn });
    for (const e of sim.step()) if (e.type === 'crashed') crashes++;
    const now = sim.mower;
    const last = route[route.length - 1];
    if (now.progress === 0 && (last?.x !== now.x || last.y !== now.y)) {
      route.push({
        x: now.x,
        y: now.y,
        fuel: Number.isFinite(now.fuel) && now.fuel < fuel ? fuel - now.fuel : 1,
      });
    }
    fuel = now.fuel;
  }
  return {
    won: sim.status === 'won',
    ticks: sim.tick,
    crashes,
    penned: sim.sheepCount()?.penned ?? 0,
    route,
  };
}
